/**
 * Road Closures layer (Ice Storm Risk → "Road Closures") — the live render of the
 * R.1f `road_closures` ingest (Ontario 511 + Alberta 511 via Castle-Rock REST;
 * DriveBC via Open511). The ambient road-network impact signal: "where are the
 * roads closed" (operator-cop-doctrine §4.5, the ice-storm "downed trees/branches
 * off roads" picture, and year-round construction/incident closures otherwise).
 *
 * Per §3B.8 closures live in the AMBIENT register: a SOLID line draping the
 * affected road SEGMENT (the line-along-the-road is this family's visual signature,
 * distinct from the outage family's area polygon) plus a "ROAD CLOSED"-style CALLOUT
 * BOX floated above it on a leader line (#127 Pinecrest mockup), NEVER pulsing,
 * coloured by REGISTER (full closure vs partial restriction) from the
 * context-locked road-closure palette (styles/roadClosureStyles). The segment line
 * is GROUND-CLAMPED (clampToGround) so it drapes the road on relief; the callout box
 * is the click target. Rows with no segment geometry render the callout alone
 * (callout-only fallback), anchored at the representative point.
 *
 * Render gate (build.md § L.2, the power-outages precedent): HIDDEN at country
 * zoom, visible regional+. Each dot + line carries a distanceDisplayCondition with
 * far = REGIONAL_VISIBLE_FAR_M so the national Default view never clutters.
 *
 * Imperative installer (MapView owns the viewer), mirroring PowerOutagesLayer:
 * requestRefresh on camera moves, setVisible on the toggle, pick from the central
 * click handler → RoadClosureFeature → the RoadClosureBody side panel.
 */
import * as Cesium from 'cesium';
import type { RoadClosureFeature, RoadClosureFeatureCollection } from '@tsr/shared-types';
import { fetchRoadClosures, type BboxQuery, type RoadClosureFetchOpts } from '../api';
import {
  roadClosureStyle,
  roadClosureLineWidth,
  ROAD_CLOSURE_LINE_ALPHA,
  ROAD_CLOSURE_CALLOUT_HEIGHT_M,
  ROAD_CLOSURE_LEADER_WIDTH,
} from '../styles/roadClosureStyles';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';
import { zoneGlyphBillboard, ZONE_GLYPH } from './zoneGlyph';

const REFRESH_DEBOUNCE_MS = 300;
const CLOSURE_PREFIX = 'road-closure:';
const CLOSURE_SEGMENT_PREFIX = 'road-closure-seg:';
const CLOSURE_LEADER_PREFIX = 'road-closure-leader:';
const CLOSURE_GLYPH_PREFIX = 'road-closure-glyph:';
const NIGHT_INK = '#0b1020';

export interface RoadClosuresLayerOptions {
  /** In-view closure count. Emits the fetched feature count on success, and **null**
   *  when the count is UNKNOWN for the current view — a fetch FAILURE (#337: so a stale
   *  count can't ride into an outage as a false "feed is live"). A pending fetch after a
   *  view change is signalled by the consumer resetting to null on bbox change. */
  onCount?: (n: number | null) => void;
  getVisible?: () => boolean;
  /** L.1 mute sub-toggle: when this returns false, lane/partial RESTRICTION dots
   *  (is_closure=false) are muted so only full closures render. Default true
   *  (restrictions shown) when not supplied. */
  getAllowRestrictions?: () => boolean;
  /** Test seam: inject a fetcher in place of the real API call. */
  fetcher?: (
    bbox: BboxQuery,
    signal: AbortSignal,
    opts?: RoadClosureFetchOpts,
  ) => Promise<RoadClosureFeatureCollection>;
}

export interface RoadClosuresLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Re-apply the restrictions mute (and any future filters) to the already-loaded
   *  features without re-fetching — called when the "Restrictions" sub-toggle flips. */
  refilter(): void;
  /** Map a picked Cesium entity back to its closure feature (null if not ours). */
  pick(entity: unknown): RoadClosureFeature | null;
  destroy(): void;
}

export function installRoadClosuresLayer(
  viewer: Cesium.Viewer,
  opts: RoadClosuresLayerOptions = {},
): RoadClosuresLayerHandle {
  const fetcher = opts.fetcher ?? fetchRoadClosures;
  const ds = new Cesium.CustomDataSource('road-closures');
  void viewer.dataSources.add(ds);

  // Hide at country zoom, show regional+ (the L.2 render gate). Shared by every
  // dot + line so the whole field tiers in together as the eye descends.
  const gate = new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M);

  const entityToFeature = new Map<Cesium.Entity, RoadClosureFeature>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  // Guards against an older in-flight load resolving after a newer one.
  let loadGen = 0;
  // Last loaded collection, so the restrictions mute can re-render without refetch.
  let lastFc: RoadClosureFeatureCollection | null = null;

  const render = (fc: RoadClosureFeatureCollection): void => {
    ds.entities.removeAll();
    entityToFeature.clear();
    const allowRestrictions = opts.getAllowRestrictions ? opts.getAllowRestrictions() : true;
    for (const feature of fc.features) {
      // L.1 mute: skip lane/partial RESTRICTION dots when restrictions are muted —
      // full closures (is_closure=true) always render. (The smoke_detected anomaly
      // surface is untouched; this is a render-only filter on this layer's own data.)
      if (!allowRestrictions && feature.properties.is_closure !== true) continue;
      const style = roadClosureStyle(feature.properties.is_closure);
      const color = Cesium.Color.fromCssColorString(style.color);
      const [lon, lat] = feature.geometry.coordinates;

      // Affected road segment (when present) — a GROUND-CLAMPED SOLID line draping the
      // road on relief (clampToGround). This is the family's primary signature (#127);
      // the callout's leader rises from the middle of it.
      const seg = feature.segment?.coordinates;
      let anchorLon = lon;
      let anchorLat = lat;
      if (seg && seg.length >= 2) {
        const positions = Cesium.Cartesian3.fromDegreesArray(seg.flat());
        const line = ds.entities.add({
          id: `${CLOSURE_SEGMENT_PREFIX}${feature.id}`,
          polyline: {
            positions,
            width: roadClosureLineWidth(feature.properties.is_closure),
            material: color.withAlpha(ROAD_CLOSURE_LINE_ALPHA),
            clampToGround: true,
            distanceDisplayCondition: gate,
          },
        });
        entityToFeature.set(line, feature);
        // Anchor the callout's leader at the middle of the closed segment.
        const mid = seg[Math.floor(seg.length / 2)];
        if (mid && mid.length >= 2) {
          anchorLon = mid[0]!;
          anchorLat = mid[1]!;
        }
      }

      // Leader line — a thin world-space stalk rising from the road anchor up to the
      // floating callout box. depthFailMaterial keeps it visible where terrain would
      // otherwise occlude it. NEVER pulses (§3B.8 motion budget).
      const foot = Cesium.Cartesian3.fromDegrees(anchorLon, anchorLat);
      const head = Cesium.Cartesian3.fromDegrees(anchorLon, anchorLat, ROAD_CLOSURE_CALLOUT_HEIGHT_M);
      const leader = ds.entities.add({
        id: `${CLOSURE_LEADER_PREFIX}${feature.id}`,
        polyline: {
          positions: [foot, head],
          width: ROAD_CLOSURE_LEADER_WIDTH,
          material: color.withAlpha(ROAD_CLOSURE_LINE_ALPHA),
          depthFailMaterial: color.withAlpha(0.5),
          distanceDisplayCondition: gate,
        },
      });
      entityToFeature.set(leader, feature);

      // The CALLOUT BOX (#127 Pinecrest mockup) — a register-coloured signage box
      // ("ROAD CLOSED" / "LANE RESTRICTED") floating atop the leader. This is the
      // primary click target; for no-segment rows it's the only mark (callout-only
      // fallback). Sits at the head of the leader, box bottom on the stalk.
      const callout = ds.entities.add({
        id: `${CLOSURE_PREFIX}${feature.id}`,
        position: head,
        label: {
          text: style.callout,
          font: '700 12px Inter, system-ui, sans-serif',
          fillColor: Cesium.Color.fromCssColorString(style.textColor),
          showBackground: true,
          backgroundColor: color.withAlpha(0.95),
          backgroundPadding: new Cesium.Cartesian2(8, 5),
          outlineColor: Cesium.Color.fromCssColorString(NIGHT_INK),
          outlineWidth: 1,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
          pixelOffset: new Cesium.Cartesian2(0, -2),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: gate,
        },
      });
      entityToFeature.set(callout, feature);

      // #475 — the BARRIER IDENTITY glyph (the CAHS road-closure mark on the act-chip
      // plate with a thin register-coloured rim) at the road anchor: SHAPE-primary
      // "this is a road closure", floated CLEAR of every zone fill (#473). Distinct
      // entity so it never disturbs the callout click target (both map back). Canvas-
      // free, so it renders in unit tests; undefined only if the glyph is unknown.
      const glyph = zoneGlyphBillboard(ZONE_GLYPH.road_closure, { rimColor: style.color });
      if (glyph) {
        const identity = ds.entities.add({
          id: `${CLOSURE_GLYPH_PREFIX}${feature.id}`,
          position: foot,
          billboard: glyph,
        });
        entityToFeature.set(identity, feature);
      }
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      lastFc = fc;
      render(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchRoadClosures failed', err);
      // #337 — the count is now UNKNOWN for this view (the feed failed). Emit null so a
      // stale prior count can't survive as a false "no closures reported — feed is live".
      opts.onCount?.(null);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      // #337 — abort any in-flight fetch for the PREVIOUS view immediately, so a stale
      // fetch can't resolve mid-debounce and emit a count for the wrong bbox (which could
      // read as "feed is live" over a covered view it doesn't belong to). The aborted
      // fetch returns early (AbortError) without emitting.
      if (inflight) inflight.abort();
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    refilter(): void {
      if (lastFc) render(lastFc);
    },
    pick(entity: unknown): RoadClosureFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
